import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Button, Spinner } from '@spark';

import { useAuth } from '@/features/auth/AuthContext.jsx';
import { get_base_url } from '@/shared/http/client';
import WaitlistLayout, { WaitlistLogo } from '@/features/waiting-list/components/WaitlistLayout';

function apiBase() {
    if (window.location.port === '8760') return '/api';
    return get_base_url(window.location.origin);
}

function sameEmail(left, right) {
    return (left || '').trim().toLowerCase() === (right || '').trim().toLowerCase();
}

function errorDetail(body, fallback) {
    if (typeof body?.detail === 'string' && body.detail) return body.detail;
    return fallback;
}

const STATUS_COPY = {
    expired: 'This invitation has expired. Ask a workspace admin to send a new one.',
    revoked: 'This invitation was revoked.',
    accepted: 'This invitation has already been accepted.',
};

/**
 * Page opened from the invitation email (`/join-workspace?token=`).
 *
 * Loading the page does not join the workspace. The invitee confirms with
 * "Join Workspace" while signed in as the address the invitation was sent to.
 */
export default function JoinWorkspacePage() {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const { user, loading } = useAuth();
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState('');
    const [previewLoading, setPreviewLoading] = useState(Boolean(token));
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState('');

    useEffect(() => {
        if (!token) {
            setPreviewLoading(false);
            return undefined;
        }
        let cancelled = false;
        setPreviewLoading(true);
        setPreviewError('');
        (async () => {
            try {
                const response = await fetch(
                    `${apiBase()}/workspaces/invitations/preview?token=${encodeURIComponent(token)}`,
                );
                const body = await response.json().catch(() => ({}));
                if (cancelled) return;
                if (!response.ok) {
                    setPreview(null);
                    setPreviewError(errorDetail(body, 'This invitation link is not valid.'));
                    return;
                }
                setPreview(body);
            } catch (_) {
                if (!cancelled) setPreviewError('Could not load this invitation.');
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const returnTo = `${location.pathname}${location.search}`;

    const signIn = useCallback(async () => {
        const next = `/login?next=${encodeURIComponent(returnTo)}`;
        if (user) {
            try {
                await fetch(`${apiBase()}/auth/logout`, { method: 'POST', credentials: 'include' });
            } catch (_) {
                // The login page still has to be the next stop.
            }
        }
        window.location.assign(next);
    }, [returnTo, user]);

    const join = useCallback(async () => {
        if (busy || !token) return;
        setBusy(true);
        setActionError('');
        try {
            const response = await fetch(`${apiBase()}/workspaces/invitations/accept`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                setActionError(errorDetail(body, 'Could not join this workspace.'));
                setBusy(false);
                return;
            }
            window.location.assign('/');
        } catch (_) {
            setActionError('Could not join this workspace.');
            setBusy(false);
        }
    }, [busy, token]);

    if (loading || previewLoading) {
        return (
            <WaitlistLayout variant="done">
                <div className="waitlist-loading">
                    <Spinner size="large" aria-label="Loading invitation" />
                </div>
            </WaitlistLayout>
        );
    }

    const workspaceName = preview?.workspace_name || 'this workspace';
    const invitedEmail = preview?.email || '';
    const signedInAsInvitee = Boolean(user?.email) && sameEmail(user.email, invitedEmail);
    const pending = preview?.status === 'pending';

    let body = previewError;
    if (!token) body = 'This invitation link is missing its token.';
    else if (preview && STATUS_COPY[preview.status]) body = STATUS_COPY[preview.status];
    else if (pending) {
        body = `You've been invited to join ${workspaceName}.`;
        if (invitedEmail) body += ` The invitation was sent to ${invitedEmail}.`;
    }

    let action = null;
    if (pending && signedInAsInvitee) {
        action = (
            <Button size="large" variant="primary" disabled={busy} onClick={join}>
                Join Workspace
            </Button>
        );
    } else if (pending && user?.email) {
        action = (
            <Button size="large" variant="primary" onClick={signIn}>
                {`Sign in as ${invitedEmail}`}
            </Button>
        );
    } else if (pending) {
        action = (
            <Button size="large" variant="primary" onClick={signIn}>
                Sign in to join
            </Button>
        );
    } else if (preview?.status === 'accepted' && signedInAsInvitee) {
        action = (
            <Button size="large" variant="primary" onClick={() => window.location.assign('/')}>
                Go to Specific
            </Button>
        );
    }

    return (
        <WaitlistLayout variant="done">
            <WaitlistLogo size="small" />
            <h1 className="waitlist-done-title waitlist-inter-light-36">Join workspace</h1>
            {body ? <p className="waitlist-done-body waitlist-inter-light-24">{body}</p> : null}
            {actionError ? <p className="waitlist-done-body waitlist-inter-light-24">{actionError}</p> : null}
            {user?.email && pending && !signedInAsInvitee ? (
                <p className="waitlist-done-body waitlist-inter-light-24">{`Signed in as ${user.email}`}</p>
            ) : null}
            {action ? <div className="waitlist-done-footer">{action}</div> : null}
        </WaitlistLayout>
    );
}
